import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store';

import { tableDataQueryKeyMap } from './use-get-table-data';
import { useTableFiltersMap } from './use-console-filter';

import type {
  IQueueTableData,
  TBaseTableQueryResponse,
} from 'infrastructure/interfaces';

interface IDueDateData {
  ccmDueDate: string;
  patientGuid: string;
}

const mutationFn = async (postData: IDueDateData) => {
  const url = 'core-2/ccm/console/due-date';
  const { data, error } = await dataService.updateOnly<{ patientGuid: string }>(
    url,
    postData,
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useSetDueDate = () => {
  const queryClient = useQueryClient();
  const tableFiltersMap = useTableFiltersMap();
  const { t } = useTranslation();
  const {
    ccmConsole: { updateQueueItem },
  } = useAppDispatch();

  const { mutate, isLoading } = useMutation({
    mutationFn,
    onSuccess(responseData, variables) {
      const newlyFilter = tableFiltersMap.newly;
      const newlyQueryKey = tableDataQueryKeyMap.newly(newlyFilter);
      const followUpFilter = tableFiltersMap['follow-up'];
      const followUpQueryKey =
        tableDataQueryKeyMap['follow-up'](followUpFilter);
      const partialsFilter = tableFiltersMap.partials;
      const partialsQueryKey = tableDataQueryKeyMap.partials(partialsFilter);

      [newlyQueryKey, followUpQueryKey, partialsQueryKey].forEach(
        (queryKey) => {
          queryClient.setQueryData<TBaseTableQueryResponse<IQueueTableData>>(
            queryKey,
            (oldData) => {
              if (!oldData) return oldData;

              const newData = structuredClone(oldData);
              newData.data?.forEach((item) => {
                if (item.guid === variables.patientGuid) {
                  item.dueDate = new Date(variables.ccmDueDate).toISOString();
                }
              });

              return newData;
            },
          );
        },
      );

      updateQueueItem({
        guid: variables.patientGuid,
        dueDate: new Date(variables.ccmDueDate).toISOString(),
      });

      notificationController.success({
        message: t('messages.baseUpdateMessage', {
          value: t('labels.dueDate'),
        }),
      });
    },
  });

  return { setDueDate: mutate, isLoading };
};
